/**
 * W2A — Workflow Actions Panel
 *
 * Embedded inside the Status edit UI. Renders Immediate + Timeline sections
 * with inline add/edit forms. DEFINITION only — nothing executes here.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, RotateCcw, ArrowUp, ArrowDown, Clock, Zap, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";


/**
 * W2C-1.7 — Compact rendered preview shown under the SMS/Email picker so an
 * admin building a workflow action can see what the selected template will
 * look like when merged with sample client data. Highlights unresolved
 * placeholders in amber; will not send anything.
 */
function TemplateRenderPreview({ preview, channel }) {
    const body = preview?.body;
    if (!body) return null;
    const highlight = (text, unresolved) => {
        if (!unresolved || unresolved.length === 0) return text;
        const parts = [];
        const re = /\{\{?\s*([a-zA-Z0-9_]+)\s*\}?\}/g;
        let last = 0;
        let m;
        while ((m = re.exec(text)) !== null) {
            if (unresolved.includes(m[1])) {
                if (m.index > last) parts.push(text.slice(last, m.index));
                parts.push(
                    <span key={m.index} className="bg-amber-200/70 text-amber-900 rounded px-0.5 font-mono">{m[0]}</span>,
                );
                last = m.index + m[0].length;
            }
        }
        if (last < text.length) parts.push(text.slice(last));
        return parts;
    };
    return (
        <div className="rounded-md border border-sky-200 bg-sky-50/40 p-3 text-xs space-y-1" data-testid="workflow-action-render-preview">
            <div className="flex items-center justify-between">
                <span className="font-medium text-sky-900">Rendered Preview (sample data)</span>
                {body.has_unresolved ? (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-800" data-testid="workflow-preview-has-unresolved">
                        {body.unresolved.length} unresolved
                    </span>
                ) : (
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">Clean</span>
                )}
            </div>
            {channel === "email" && preview?.subject && (
                <div className="text-slate-900">
                    <span className="text-slate-500">Subject:</span> {highlight(preview.subject.rendered || "", preview.subject.unresolved || [])}
                </div>
            )}
            <pre className="whitespace-pre-wrap break-words font-mono text-slate-900" data-testid="workflow-preview-body">
                {highlight(body.rendered || "", body.unresolved || [])}
            </pre>
            {body.has_unresolved && (
                <div className="text-[10px] text-amber-900" data-testid="workflow-preview-warning">
                    Unresolved fields will block automated sends in W2C-2 until fixed on the template.
                </div>
            )}
        </div>
    );
}


const ACTION_TYPE_LABELS = {
    send_sms: "Send SMS to Client",
    send_email: "Send Email to Client",
    notify_user: "Notify User",
    add_internal_note: "Add Internal Note",
    change_workflow_status: "Change Workflow Status",
};

const IMMEDIATE_ACTION_TYPES = ["send_sms", "send_email", "notify_user", "add_internal_note"];
const TIMELINE_ACTION_TYPES = [
    "send_sms", "send_email", "notify_user", "add_internal_note", "change_workflow_status",
];

const NOTIFY_TARGET_LABELS = {
    processor: "Processor",
    sales_rep: "Sales Rep",
    specific_user: "Specific User",
    admins: "All Admins",
};

const summarizeAction = (a) => {
    switch (a.action_type) {
        case "send_sms": {
            const tpl = a.sms_template_name || a.sms_template_id;
            return tpl ? `Template: ${tpl}` : "Template not selected";
        }
        case "send_email": {
            const tpl = a.email_template_name || a.email_template_id;
            return tpl ? `Template: ${tpl}` : "Template not selected";
        }
        case "notify_user": {
            if (a.notification_target === "specific_user") return `Notify ${a.target_user_name || a.target_user_id || "…"}`;
            return `Notify ${NOTIFY_TARGET_LABELS[a.notification_target] || a.notification_target || "…"}`;
        }
        case "add_internal_note":
            return `“${(a.note_body || "").slice(0, 60)}${(a.note_body || "").length > 60 ? "…" : ""}”`;
        case "change_workflow_status":
            return `→ ${a.target_workflow_status_name || a.target_workflow_status_id || "…"}`;
        default:
            return "";
    }
};

const summarizeTimeline = (a) => {
    const days = a.delay_count ?? "?";
    const time = a.run_time || "10:00";
    const only = a.only_if_still_in_status !== false ? " (only if still in status)" : "";
    return `After ${days} business day${days === 1 ? "" : "s"} at ${time}${only}`;
};

export default function WorkflowActionsPanel({ statusId, allStatuses }) {
    const [immediate, setImmediate] = useState([]);
    const [timeline, setTimeline] = useState([]);
    const [users, setUsers] = useState([]);
    const [smsTemplates, setSmsTemplates] = useState([]);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    // Only ONE editor open at a time — either "add-<trigger>" or the action id.
    const [openEditor, setOpenEditor] = useState(null);
    const [editingAction, setEditingAction] = useState(null);
    // W2A.1 — CRM confirmation state. `null` = closed.
    const [confirmState, setConfirmState] = useState(null);

    const load = useCallback(async () => {
        if (!statusId) return;
        setLoading(true);
        try {
            const r = await api.get(`/workflow/statuses/${statusId}/actions?include_inactive=true`);
            setImmediate(r.data?.immediate || []);
            setTimeline(r.data?.timeline || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [statusId]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            // Split fetches so a single failure does not zero out the other two.
            // Notably: /users/staff was previously requested as /users, which 404'd
            // and rejected the whole Promise.all — silently emptying the SMS &
            // email pickers even when eligible workflow templates existed.
            const settle = async (url, setter) => {
                try {
                    const { data } = await api.get(url);
                    if (!cancelled) setter(data || []);
                } catch (e) {
                    console.error(`[WorkflowActionsPanel] failed to load ${url}`, e?.response?.status || e?.message);
                }
            };
            await Promise.all([
                settle("/users/staff", setUsers),
                // W2C-1.5: only fetch approved client-workflow SMS templates linked to an active route.
                settle("/message-templates?picker=workflow_sms", setSmsTemplates),
                settle("/message-templates?picker=workflow_email", setEmailTemplates),
            ]);
        })();
        return () => { cancelled = true; };
    }, []);

    const openAdd = (trigger) => {
        setEditingAction(null);
        setOpenEditor(`add-${trigger}`);
    };
    const openEdit = (a) => {
        setEditingAction(a);
        setOpenEditor(a.id);
    };
    const closeEditor = () => { setOpenEditor(null); setEditingAction(null); };

    const saved = async () => { closeEditor(); await load(); };

    const runDeactivate = async (a) => {
        try {
            await api.patch(`/workflow/actions/${a.id}/deactivate`);
            toast.success("Action deactivated");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const runDelete = async (a) => {
        try {
            await api.delete(`/workflow/actions/${a.id}`);
            toast.success("Action deleted");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const activate = async (a) => {
        try {
            await api.patch(`/workflow/actions/${a.id}/activate`);
            toast.success("Action re-activated");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const move = async (a, direction) => {
        try {
            await api.post(`/workflow/actions/${a.id}/move?direction=${direction}`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="space-y-5" data-testid="workflow-actions-panel">
            {/* ─── Immediate ─────────────────────────────────────────── */}
            <section data-testid="immediate-actions-section">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <Zap className="h-4 w-4 text-amber-600" />
                        <h4 className="font-semibold text-sm text-gray-900">Run Immediately When Client Enters This Status</h4>
                    </div>
                    <Button
                        size="sm" variant="outline"
                        onClick={() => openAdd("immediate")}
                        disabled={!!openEditor}
                        data-testid="add-immediate-action-btn"
                    >
                        <Plus className="h-3.5 w-3.5 mr-1" /> Add action
                    </Button>
                </div>
                {loading ? <div className="text-xs text-muted-foreground">Loading…</div> : (
                    <div className="space-y-2">
                        {immediate.length === 0 && openEditor !== "add-immediate" && (
                            <div className="text-xs text-muted-foreground italic border border-dashed border-slate-200 rounded px-3 py-2">
                                No immediate actions yet.
                            </div>
                        )}
                        {immediate.map((a, i) => (
                            openEditor === a.id ? (
                                <ActionEditor
                                    key={a.id}
                                    statusId={statusId}
                                    trigger="immediate"
                                    initial={a}
                                    allStatuses={allStatuses}
                                    users={users}
                                    smsTemplates={smsTemplates}
                                    emailTemplates={emailTemplates}
                                    onCancel={closeEditor}
                                    onSaved={saved}
                                />
                            ) : (
                                <ActionCard
                                    key={a.id}
                                    action={a}
                                    trigger="immediate"
                                    index={i}
                                    total={immediate.length}
                                    onEdit={() => openEdit(a)}
                                    onMove={(dir) => move(a, dir)}
                                    onDeactivate={() => setConfirmState({ kind: "deactivate", action: a })}
                                    onDelete={() => setConfirmState({ kind: "delete", action: a })}
                                    onActivate={() => activate(a)}
                                    disabled={!!openEditor}
                                />
                            )
                        ))}
                        {openEditor === "add-immediate" && (
                            <ActionEditor
                                statusId={statusId}
                                trigger="immediate"
                                initial={null}
                                allStatuses={allStatuses}
                                users={users}
                                smsTemplates={smsTemplates}
                                emailTemplates={emailTemplates}
                                onCancel={closeEditor}
                                onSaved={saved}
                            />
                        )}
                    </div>
                )}
            </section>

            {/* ─── Timeline ──────────────────────────────────────────── */}
            <section data-testid="timeline-actions-section">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-indigo-600" />
                        <h4 className="font-semibold text-sm text-gray-900">Actions Timeline</h4>
                    </div>
                    <Button
                        size="sm" variant="outline"
                        onClick={() => openAdd("timeline")}
                        disabled={!!openEditor}
                        data-testid="add-timeline-action-btn"
                    >
                        <Plus className="h-3.5 w-3.5 mr-1" /> Add action
                    </Button>
                </div>
                {loading ? <div className="text-xs text-muted-foreground">Loading…</div> : (
                    <div className="space-y-2">
                        {timeline.length === 0 && openEditor !== "add-timeline" && (
                            <div className="text-xs text-muted-foreground italic border border-dashed border-slate-200 rounded px-3 py-2">
                                No timeline actions yet. Each timeline action runs after a business-day delay when the client enters this status.
                            </div>
                        )}
                        {timeline.map((a, i) => (
                            openEditor === a.id ? (
                                <ActionEditor
                                    key={a.id}
                                    statusId={statusId}
                                    trigger="timeline"
                                    initial={a}
                                    allStatuses={allStatuses}
                                    users={users}
                                    smsTemplates={smsTemplates}
                                    emailTemplates={emailTemplates}
                                    onCancel={closeEditor}
                                    onSaved={saved}
                                />
                            ) : (
                                <ActionCard
                                    key={a.id}
                                    action={a}
                                    trigger="timeline"
                                    index={i}
                                    total={timeline.length}
                                    onEdit={() => openEdit(a)}
                                    onMove={(dir) => move(a, dir)}
                                    onDeactivate={() => setConfirmState({ kind: "deactivate", action: a })}
                                    onDelete={() => setConfirmState({ kind: "delete", action: a })}
                                    onActivate={() => activate(a)}
                                    disabled={!!openEditor}
                                />
                            )
                        ))}
                        {openEditor === "add-timeline" && (
                            <ActionEditor
                                statusId={statusId}
                                trigger="timeline"
                                initial={null}
                                allStatuses={allStatuses}
                                users={users}
                                smsTemplates={smsTemplates}
                                emailTemplates={emailTemplates}
                                onCancel={closeEditor}
                                onSaved={saved}
                            />
                        )}
                    </div>
                )}
            </section>

            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground font-semibold border-t border-slate-100 pt-3">
                Definitions only — actions do not execute yet. Execution will land in W2B.
            </div>

            <ConfirmActionDialog
                state={confirmState}
                onClose={() => setConfirmState(null)}
                onConfirm={async () => {
                    const s = confirmState;
                    setConfirmState(null);
                    if (!s) return;
                    if (s.kind === "deactivate") await runDeactivate(s.action);
                    else if (s.kind === "delete") await runDelete(s.action);
                }}
            />
        </div>
    );
}

function ConfirmActionDialog({ state, onClose, onConfirm }) {
    const isDelete = state?.kind === "delete";
    const label = state ? (ACTION_TYPE_LABELS[state.action.action_type] || state.action.action_type) : "";
    const summary = state ? summarizeAction(state.action) : "";
    return (
        <AlertDialog open={!!state} onOpenChange={(o) => { if (!o) onClose(); }}>
            <AlertDialogContent data-testid={isDelete ? "confirm-delete-action-dialog" : "confirm-deactivate-action-dialog"}>
                <AlertDialogHeader>
                    <AlertDialogTitle>
                        {isDelete ? "Delete workflow action?" : "Deactivate workflow action?"}
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                        {isDelete
                            ? "This permanently removes this workflow action definition. This cannot be undone."
                            : "This action will stop being used in future workflow automation definitions. You can reactivate it later."}
                    </AlertDialogDescription>
                </AlertDialogHeader>
                {state && (
                    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                        <div className="font-medium text-gray-900">{label}</div>
                        <div className="text-xs text-muted-foreground">{summary}</div>
                    </div>
                )}
                <AlertDialogFooter>
                    <AlertDialogCancel data-testid="confirm-action-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={onConfirm}
                        className={isDelete
                            ? "bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
                            : ""}
                        data-testid={isDelete ? "confirm-action-delete" : "confirm-action-deactivate"}
                    >
                        {isDelete ? "Delete" : "Deactivate"}
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}

function ActionCard({ action, trigger, index, total, onEdit, onMove, onDeactivate, onDelete, onActivate, disabled }) {
    const label = ACTION_TYPE_LABELS[action.action_type] || action.action_type;
    const summary = summarizeAction(action);
    const testKey = `${trigger}-${action.action_type}-${index}`;
    return (
        <div
            className={`rounded-lg border ${action.active ? "border-border bg-white" : "border-slate-200 bg-slate-50/60"} shadow-sm px-3 py-2 flex flex-wrap items-center gap-3`}
            data-testid={`action-row-${testKey}`}
        >
            <div className="flex-1 min-w-[200px]">
                {trigger === "timeline" && (
                    <div className="text-[11px] uppercase tracking-wide text-indigo-700 font-semibold">
                        {summarizeTimeline(action)}
                    </div>
                )}
                <div className="text-sm font-medium text-gray-900">{label}</div>
                <div className="text-xs text-muted-foreground">{summary}</div>
                {!action.active && <div className="text-[10.5px] uppercase tracking-wider text-rose-700 font-semibold mt-0.5">Deactivated</div>}
            </div>
            <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" disabled={disabled || index === 0} onClick={() => onMove("up")} data-testid={`move-up-${testKey}`}><ArrowUp className="h-3.5 w-3.5" /></Button>
                <Button size="icon" variant="ghost" disabled={disabled || index === total - 1} onClick={() => onMove("down")} data-testid={`move-down-${testKey}`}><ArrowDown className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="outline" disabled={disabled} onClick={onEdit} data-testid={`edit-action-${testKey}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                {action.active
                    ? <Button size="sm" variant="ghost" disabled={disabled} onClick={onDeactivate} data-testid={`deactivate-action-${testKey}`}><X className="h-3.5 w-3.5 mr-1" /> Deactivate</Button>
                    : <Button size="sm" variant="ghost" disabled={disabled} onClick={onActivate} data-testid={`activate-action-${testKey}`}><RotateCcw className="h-3.5 w-3.5 mr-1" /> Activate</Button>}
                <Button size="sm" variant="ghost" disabled={disabled} onClick={onDelete} className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" data-testid={`delete-action-${testKey}`}><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
            </div>
        </div>
    );
}

function ActionEditor({
    statusId, trigger, initial, allStatuses, users, smsTemplates, emailTemplates, onCancel, onSaved,
}) {
    const [form, setForm] = useState(() => ({
        action_type: initial?.action_type || (trigger === "timeline" ? "send_sms" : "send_sms"),
        title: initial?.title || "",
        description: initial?.description || "",
        active: initial?.active ?? true,
        delay_count: initial?.delay_count ?? (trigger === "timeline" ? 1 : null),
        delay_unit: initial?.delay_unit || "business_days",
        run_time: initial?.run_time || "10:00",
        timezone: initial?.timezone || "",
        only_if_still_in_status: initial?.only_if_still_in_status ?? true,
        sms_template_id: initial?.sms_template_id || "",
        sms_template_name: initial?.sms_template_name || "",
        email_template_id: initial?.email_template_id || "",
        email_template_name: initial?.email_template_name || "",
        notification_target: initial?.notification_target || "processor",
        target_user_id: initial?.target_user_id || "",
        target_user_name: initial?.target_user_name || "",
        target_workflow_status_id: initial?.target_workflow_status_id || "",
        target_workflow_status_name: initial?.target_workflow_status_name || "",
        note_body: initial?.note_body || "",
    }));
    const [busy, setBusy] = useState(false);
    // W2C-1.7 — fetch a rendered preview whenever the selected template changes.
    const [tplPreview, setTplPreview] = useState(null);
    useEffect(() => {
        const tid = form.action_type === "send_sms" ? form.sms_template_id
                  : form.action_type === "send_email" ? form.email_template_id
                  : null;
        if (!tid) { setTplPreview(null); return; }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.post(`/message-templates/${tid}/render-preview`, {});
                if (!cancelled) setTplPreview(data);
            } catch (e) {
                if (!cancelled) setTplPreview(null);
                console.error("[WorkflowActionsPanel] render-preview failed", e?.response?.status || e?.message);
            }
        })();
        return () => { cancelled = true; };
    }, [form.action_type, form.sms_template_id, form.email_template_id]);

    const availableTypes = trigger === "timeline" ? TIMELINE_ACTION_TYPES : IMMEDIATE_ACTION_TYPES;

    const otherStatuses = useMemo(
        () => (allStatuses || []).filter(s => s.id !== statusId),
        [allStatuses, statusId],
    );

    // ─── W2A.1 client-side validation ────────────────────────────
    const errors = useMemo(() => {
        const errs = {};
        if (trigger === "timeline") {
            const raw = form.delay_count;
            if (raw === "" || raw === null || raw === undefined) {
                errs.delay_count = "Days is required.";
            } else if (Number.isNaN(Number(raw))) {
                errs.delay_count = "Days must be a number.";
            } else if (!Number.isInteger(Number(raw))) {
                errs.delay_count = "Days must be a whole number.";
            } else if (Number(raw) < 0) {
                errs.delay_count = "Days must be 0 or greater.";
            }
            if (!(form.run_time || "").trim()) errs.run_time = "Run time is required.";
        }
        if (form.action_type === "send_sms") {
            if (!(form.sms_template_id || "").trim() && !(form.sms_template_name || "").trim()) {
                errs.sms = "Pick a template or enter a label.";
            }
        } else if (form.action_type === "send_email") {
            if (!(form.email_template_id || "").trim() && !(form.email_template_name || "").trim()) {
                errs.email = "Pick a template or enter a label.";
            }
        } else if (form.action_type === "notify_user") {
            if (!form.notification_target) errs.notification_target = "Choose who to notify.";
            if (form.notification_target === "specific_user" && !form.target_user_id) {
                errs.target_user_id = "Choose a user.";
            }
        } else if (form.action_type === "add_internal_note") {
            if (!(form.note_body || "").trim()) errs.note_body = "Note body cannot be blank.";
        } else if (form.action_type === "change_workflow_status") {
            if (!form.target_workflow_status_id) errs.target_workflow_status_id = "Choose a target status.";
            if (trigger !== "timeline") errs.action_type = "Change Workflow Status is only allowed for timeline actions.";
        }
        return errs;
    }, [form, trigger]);

    const isValid = Object.keys(errors).length === 0;

    const setSmsTemplate = (id) => {
        const t = smsTemplates.find(x => x.id === id);
        setForm(f => ({ ...f, sms_template_id: id, sms_template_name: t?.template_name || t?.name || "" }));
    };
    const setEmailTemplate = (id) => {
        const t = emailTemplates.find(x => x.id === id);
        setForm(f => ({ ...f, email_template_id: id, email_template_name: t?.template_name || t?.name || "" }));
    };
    const setSpecificUser = (id) => {
        const u = users.find(x => x.id === id);
        setForm(f => ({ ...f, target_user_id: id, target_user_name: u?.name || u?.email || "" }));
    };
    const setTargetStatus = (id) => {
        const s = (allStatuses || []).find(x => x.id === id);
        setForm(f => ({
            ...f,
            target_workflow_status_id: id,
            target_workflow_status_name: s?.name || "",
        }));
    };

    const save = async () => {
        if (!isValid) {
            const first = Object.values(errors)[0];
            if (first) toast.error(first);
            return;
        }
        // Build payload — omit fields irrelevant to the chosen action_type.
        const payload = {
            trigger_type: trigger,
            action_type: form.action_type,
            title: form.title,
            description: form.description,
            active: form.active,
        };
        if (trigger === "timeline") {
            payload.delay_count = Number(form.delay_count);
            payload.delay_unit = form.delay_unit || "business_days";
            payload.run_time = form.run_time || "10:00";
            if (form.timezone) payload.timezone = form.timezone;
            payload.only_if_still_in_status = !!form.only_if_still_in_status;
        }
        if (form.action_type === "send_sms") {
            payload.sms_template_id = form.sms_template_id || null;
            payload.sms_template_name = form.sms_template_name || null;
        } else if (form.action_type === "send_email") {
            payload.email_template_id = form.email_template_id || null;
            payload.email_template_name = form.email_template_name || null;
        } else if (form.action_type === "notify_user") {
            payload.notification_target = form.notification_target;
            if (form.notification_target === "specific_user") {
                payload.target_user_id = form.target_user_id;
                payload.target_user_name = form.target_user_name;
            }
        } else if (form.action_type === "add_internal_note") {
            payload.note_body = form.note_body;
        } else if (form.action_type === "change_workflow_status") {
            payload.target_workflow_status_id = form.target_workflow_status_id;
            payload.target_workflow_status_name = form.target_workflow_status_name;
        }
        setBusy(true);
        try {
            if (initial?.id) {
                await api.put(`/workflow/actions/${initial.id}`, payload);
                toast.success("Action updated");
            } else {
                await api.post(`/workflow/statuses/${statusId}/actions`, payload);
                toast.success("Action created");
            }
            onSaved && onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <div className="rounded-lg border-2 border-indigo-200 bg-indigo-50/40 p-3 space-y-3" data-testid={`action-editor-${trigger}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Action Type">
                    <Select value={form.action_type} onValueChange={(v) => setForm({ ...form, action_type: v })}>
                        <SelectTrigger data-testid="action-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {availableTypes.map(t => (
                                <SelectItem key={t} value={t}>{ACTION_TYPE_LABELS[t]}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
                <Field label="Title (optional)">
                    <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Short label" data-testid="action-title" />
                </Field>
            </div>

            {trigger === "timeline" && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Field label="Delay (business days)" error={errors.delay_count}>
                        <Input
                            type="number" min="0"
                            value={form.delay_count ?? ""}
                            onChange={(e) => setForm({ ...form, delay_count: e.target.value === "" ? "" : Number(e.target.value) })}
                            data-testid="action-delay-count"
                        />
                    </Field>
                    <Field label="Run Time (local)" error={errors.run_time}>
                        <Input
                            type="time"
                            value={form.run_time}
                            onChange={(e) => setForm({ ...form, run_time: e.target.value })}
                            data-testid="action-run-time"
                        />
                    </Field>
                    <Field label="Timezone (IANA)">
                        <Input
                            value={form.timezone}
                            onChange={(e) => setForm({ ...form, timezone: e.target.value })}
                            placeholder="uses company default"
                            data-testid="action-timezone"
                        />
                    </Field>
                    <Field label="Only If Still In Status">
                        <Select
                            value={form.only_if_still_in_status ? "yes" : "no"}
                            onValueChange={(v) => setForm({ ...form, only_if_still_in_status: v === "yes" })}
                        >
                            <SelectTrigger data-testid="action-only-if-still"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="yes">Yes</SelectItem>
                                <SelectItem value="no">No</SelectItem>
                            </SelectContent>
                        </Select>
                    </Field>
                </div>
            )}

            {/* ─── Per-type fields ────────────────────────────── */}
            {form.action_type === "send_sms" && (
                <Field label="SMS Template (required)" error={errors.sms}>
                    {smsTemplates.length > 0 ? (
                        <Select value={form.sms_template_id || ""} onValueChange={setSmsTemplate}>
                            <SelectTrigger data-testid="action-sms-template"><SelectValue placeholder="Choose an SMS template" /></SelectTrigger>
                            <SelectContent>
                                {smsTemplates.map(t => (
                                    <SelectItem key={t.id} value={t.id}>
                                        {t.template_name || t.name || t.template_key}
                                        {t.provider_route_name ? ` · ${t.provider_route_name}` : ""}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <div className="rounded-md border border-amber-200 bg-amber-50/70 p-3 text-xs text-amber-900" data-testid="action-sms-template-empty">
                            <div className="font-medium mb-1">No workflow-eligible SMS templates yet.</div>
                            <p className="mb-2">
                                To appear here a template must have: channel=SMS, audience=Client, purpose=Client workflow, Active, Approved, and a linked active Twilio Messaging Route.
                            </p>
                            <a
                                href="/templates"
                                onClick={(e) => { e.preventDefault(); window.location.href = "/templates"; }}
                                className="underline font-medium"
                            >
                                Open Message Templates →
                            </a>
                            <Input
                                className="mt-2"
                                value={form.sms_template_name || ""}
                                onChange={(e) => setForm({ ...form, sms_template_name: e.target.value })}
                                placeholder="(Optional) Manual label — action stays disabled until real template exists"
                                data-testid="action-sms-template-manual"
                            />
                        </div>
                    )}
                </Field>
            )}
            {form.action_type === "send_sms" && tplPreview && (
                <TemplateRenderPreview preview={tplPreview} channel="sms" />
            )}
            {form.action_type === "send_email" && (
                <Field label="Email Template (required)" error={errors.email}>
                    {emailTemplates.length > 0 ? (
                        <Select value={form.email_template_id || ""} onValueChange={setEmailTemplate}>
                            <SelectTrigger data-testid="action-email-template"><SelectValue placeholder="Choose an email template" /></SelectTrigger>
                            <SelectContent>
                                {emailTemplates.map(t => (
                                    <SelectItem key={t.id} value={t.id}>
                                        {t.template_name || t.name || t.template_key}
                                        {t.provider_route_name ? ` · ${t.provider_route_name}` : ""}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <div className="rounded-md border border-amber-200 bg-amber-50/70 p-3 text-xs text-amber-900" data-testid="action-email-template-empty">
                            <div className="font-medium mb-1">No workflow-eligible Email templates yet.</div>
                            <p className="mb-2">
                                To appear here a template must have: channel=Email, audience=Client, purpose=Client workflow, Active, Approved, and a linked active SendGrid route.
                            </p>
                            <a
                                href="/templates"
                                onClick={(e) => { e.preventDefault(); window.location.href = "/templates"; }}
                                className="underline font-medium"
                            >
                                Open Message Templates →
                            </a>
                            <Input
                                className="mt-2"
                                value={form.email_template_name || ""}
                                onChange={(e) => setForm({ ...form, email_template_name: e.target.value })}
                                placeholder="(Optional) Manual label — action stays disabled until real template exists"
                                data-testid="action-email-template-manual"
                            />
                        </div>
                    )}
                </Field>
            )}
            {form.action_type === "send_email" && tplPreview && (
                <TemplateRenderPreview preview={tplPreview} channel="email" />
            )}
            {form.action_type === "notify_user" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Notify" error={errors.notification_target}>
                        <Select value={form.notification_target} onValueChange={(v) => setForm({ ...form, notification_target: v })}>
                            <SelectTrigger data-testid="action-notify-target"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="processor">Processor</SelectItem>
                                <SelectItem value="sales_rep">Sales Rep</SelectItem>
                                <SelectItem value="admins">All Admins</SelectItem>
                                <SelectItem value="specific_user">Specific User</SelectItem>
                            </SelectContent>
                        </Select>
                    </Field>
                    {form.notification_target === "specific_user" && (
                        <Field label="Specific User" error={errors.target_user_id}>
                            <Select value={form.target_user_id || ""} onValueChange={setSpecificUser}>
                                <SelectTrigger data-testid="action-notify-specific-user"><SelectValue placeholder="Pick a user" /></SelectTrigger>
                                <SelectContent>
                                    {users.map(u => (
                                        <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                    )}
                </div>
            )}
            {form.action_type === "add_internal_note" && (
                <Field label="Note Body" error={errors.note_body}>
                    <Textarea
                        rows={3}
                        value={form.note_body}
                        onChange={(e) => setForm({ ...form, note_body: e.target.value })}
                        placeholder="This note will be added to the client when the trigger fires (in a later phase)."
                        data-testid="action-note-body"
                    />
                </Field>
            )}
            {form.action_type === "change_workflow_status" && (
                <Field label="Move Client To" error={errors.target_workflow_status_id}>
                    <Select value={form.target_workflow_status_id || ""} onValueChange={setTargetStatus}>
                        <SelectTrigger data-testid="action-target-status"><SelectValue placeholder="Choose a workflow status" /></SelectTrigger>
                        <SelectContent>
                            {otherStatuses.map(s => (
                                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
            )}

            <div className="flex items-center justify-between gap-2 pt-1">
                <div className="text-xs text-rose-600 min-h-[16px]" data-testid="action-editor-error">
                    {!isValid && Object.values(errors)[0]}
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={onCancel} disabled={busy} data-testid="action-editor-cancel">Cancel</Button>
                    <Button onClick={save} disabled={busy || !isValid} data-testid="action-editor-save">
                        {busy ? "Saving…" : initial?.id ? "Save" : "Add action"}
                    </Button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, error, children }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 font-semibold">{label}</div>
            {children}
            {error && <div className="text-[11px] text-rose-600 mt-1">{error}</div>}
        </label>
    );
}
